import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import type { ApiErrorResponse, RefreshTokenResponse } from '@/types';
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from './tokenStore';

/**
 * Axios instance configured for the CRMClinica API.
 *
 * - Base URL comes from VITE_API_URL env variable (defaults to /api).
 * - Request interceptor attaches JWT from in-memory token store.
 * - Response interceptor handles 401 with token refresh.
 */
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15_000,
});

/**
 * Request interceptor: attach access token and tenant context headers.
 */
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (config.headers) {
      const token = getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      const tenantId = import.meta.env.VITE_TENANT_ID;
      if (tenantId && !config.headers['X-Tenant-Id']) {
        config.headers['X-Tenant-Id'] = tenantId;
      }
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

/**
 * Tracks whether a token refresh is already in flight
 * to prevent duplicate refresh calls from concurrent requests.
 */
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null): void {
  for (const promise of failedQueue) {
    if (error) {
      promise.reject(error);
    } else if (token) {
      promise.resolve(token);
    }
  }
  failedQueue = [];
}

/**
 * Response interceptor: handle 401 responses by attempting token refresh.
 */
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorResponse>) => {
    const originalRequest = error.config;

    if (!originalRequest || error.response?.status !== 401) {
      return Promise.reject(error);
    }

    // Avoid infinite loop on refresh endpoint itself
    if (originalRequest.url?.includes('/auth/refresh')) {
      clearTokens();
      window.location.href = '/login';
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        }
        return api(originalRequest);
      });
    }

    isRefreshing = true;

    try {
      const currentRefreshToken = getRefreshToken();
      if (!currentRefreshToken) {
        throw new Error('No refresh token available');
      }

      const { data } = await axios.post<RefreshTokenResponse>(
        `${import.meta.env.VITE_API_URL ?? '/api'}/auth/refresh`,
        { refreshToken: currentRefreshToken },
      );

      setTokens(data.accessToken, data.refreshToken);
      processQueue(null, data.accessToken);

      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
      }

      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearTokens();
      window.location.href = '/login';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default api;
